import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const GROUPS = [
  { key: "fr-gen", label: "Francophone — Général", track: "francophone", category: "general" },
  { key: "fr-tech", label: "Francophone — Technique & Professionnel", track: "francophone", category: "technical" },
  { key: "en-gen", label: "Anglophone — General", track: "anglophone", category: "general" },
  { key: "en-tech", label: "Anglophone — Technical & Vocational", track: "anglophone", category: "technical" },
];

export default function Onboarding() {
  const { refreshUser, user } = useAuth();
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filiere, setFiliere] = useState(null);
  const [serie, setSerie] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/exams").then((r) => setExams(r.data));
    api.get("/filieres").then((r) => setFilieres(r.data));
  }, []);

  useEffect(() => {
    if (user && user.role !== "student") navigate("/app", { replace: true });
  }, [user, navigate]);

  const selectedExam = exams.find((e) => e.id === selected);
  const isTechnical = selectedExam?.category === "technical";
  const selFil = filieres.find((f) => f.id === filiere);
  const generalSeries = !isTechnical && selectedExam?.series?.length ? selectedExam.series : null;
  const needsFiliere = isTechnical && !filiere;
  const needsSerie = (isTechnical && filiere && !serie) || (!!generalSeries && !serie);

  const save = async () => {
    if (!selected || needsFiliere || needsSerie) return;
    setSaving(true);
    try {
      await api.put("/auth/me", {
        exam_id: selected,
        filiere_id: isTechnical ? filiere : null,
        serie: serie || null,
      });
      await refreshUser();
      toast.success("Parfait ! Votre espace est prêt.");
      navigate("/app");
    } catch (err) {
      toast.error("Échec de l'enregistrement. Réessayez.");
    } finally {
      setSaving(false);
    }
  };

  const pickExam = (id) => { setSelected(id); setFiliere(null); setSerie(null); };

  return (
    <div className="min-h-screen bg-cream">
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
        <Logo />
        <div className="mt-10 animate-fade-up">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Bienvenue {user?.name?.split(" ")[0]}</span>
          <h1 className="mt-3 font-display text-3xl font-extrabold sm:text-4xl">Quel examen préparez-vous ?</h1>
          <p className="mt-3 text-ink/70">Nous personnaliserons vos cours, exercices et votre Professeur IA en fonction de votre choix.</p>
        </div>

        <div className="mt-10 space-y-8">
          {GROUPS.map((g) => {
            const list = exams.filter((e) => e.track === g.track && e.category === g.category);
            if (!list.length) return null;
            return (
              <div key={g.key}>
                <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-forest">{g.label}</h2>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {list.map((e) => (
                    <button key={e.id} data-testid={`exam-option-${e.id}`} onClick={() => pickExam(e.id)}
                      className={`relative rounded-2xl border-2 p-4 text-left transition ${selected === e.id ? "border-terracotta bg-terracotta/5 shadow-[0_10px_28px_rgba(255,75,10,0.18)]" : "border-border bg-white hover:border-terracotta/40"}`}>
                      {selected === e.id && <CheckCircle weight="fill" className="absolute right-3 top-3 text-terracotta" size={22} />}
                      <div className="h-2 w-10 rounded-full" style={{ background: e.color }} />
                      <div className="mt-3 font-display text-lg font-bold">{e.name}</div>
                      <div className="text-xs text-muted-foreground">{e.classe}</div>
                      <div className="mt-1 text-sm text-ink/60">{e.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {isTechnical && (
          <div className="mt-10 animate-fade-up">
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-terracotta">Choisissez votre filière technique</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filieres.map((f) => (
                <button key={f.id} data-testid={`filiere-option-${f.id}`} onClick={() => { setFiliere(f.id); setSerie(null); }}
                  className={`rounded-2xl border-2 p-4 text-left transition ${filiere === f.id ? "border-terracotta bg-terracotta/5" : "border-border bg-white hover:border-terracotta/40"}`}>
                  <div className="h-2 w-10 rounded-full" style={{ background: f.color }} />
                  <div className="mt-3 font-display text-base font-bold">{f.name}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{f.series.map((s) => s.code).join(" · ")}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {isTechnical && selFil && (
          <div className="mt-8 animate-fade-up">
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-forest">Série</h2>
            <div className="flex flex-wrap gap-2">
              {selFil.series.map((s) => (
                <button key={s.code} data-testid={`serie-option-${s.code}`} onClick={() => setSerie(s.code)}
                  className={`rounded-full border-2 px-4 py-2 text-sm font-semibold transition ${serie === s.code ? "border-terracotta bg-terracotta text-white" : "border-border bg-white hover:bg-sand"}`}>
                  {s.code} — {s.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {generalSeries && (
          <div className="mt-8 animate-fade-up">
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-forest">Choisissez votre série</h2>
            <div className="flex flex-wrap gap-2">
              {generalSeries.map((s) => (
                <button key={s} data-testid={`serie-gen-${s}`} onClick={() => setSerie(s)}
                  className={`rounded-full border-2 px-5 py-2.5 text-sm font-bold transition ${serie === s ? "border-terracotta bg-terracotta text-white" : "border-border bg-white hover:bg-sand"}`}>
                  Série {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="sticky bottom-4 mt-10">
          <Button data-testid="onboarding-continue" onClick={save} disabled={!selected || needsFiliere || needsSerie || saving}
            className="w-full rounded-full bg-terracotta py-6 text-sm font-semibold text-white hover:bg-[#E03E00]">
            {saving ? "Préparation de votre espace…" : needsFiliere ? "Choisissez une filière" : needsSerie ? "Choisissez une série" : <>Continuer <ArrowRight weight="bold" className="ml-2" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
